import { type AsyncResult, SynchrounousState, createResolvedAsyncResult } from "@/cache/AsyncResult";
import { networkCaches } from "@/cache/GlobalCache";
import { emptyFeatureCollection, emptyGeoJsonSource } from "@/geojson";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import { LayerBase } from "@/layers/LayerBase";
import { clearLayerLoading, reportLayerLoading } from "@/layers/geojson/layerLoadingStore";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import type { PoiLayer } from "@/reducer/client-models/PoiLayer";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import type { Area, BaseRequest, CoordinateSystem, GeoJSONFeatureCollection } from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type {
  BarbsLayer,
  EarthquakeLayer,
  GridLayer,
  GuiTimeZone,
  IsoLinesLayer,
  JetStreamsLayer,
  LightningLayer,
  PressureSystemLayer,
  StationLayer,
  SymbolLayer,
  ThunderstormLayer,
  TropicalCycloneLayer,
  WeatherFrontsLayer,
  WindAnimationLayer,
} from "@mm/metx-workbench.meteomatics.com";
import { debounce } from "lodash";
import type { DateTime } from "luxon";
import type { GeoJSONSource, LayoutSpecification, PaintSpecification } from "mapbox-gl";
import { computeGridDimensionFromStep } from "../utility/getGridBoundingBox";
import { getAreaFromMap } from "../utility/mapboxArea";
import type { IsoBandsLayer } from "./isoLayerShared";

const logger = Logger.fromFilename(__filename);

export type SupportedGeoJSONLayers =
  | GridLayer
  | SymbolLayer
  | BarbsLayer
  | IsoLinesLayer
  | IsoBandsLayer
  | LightningLayer
  | StationLayer
  | PoiLayer
  | PressureSystemLayer
  | TropicalCycloneLayer
  | WeatherFrontsLayer
  | JetStreamsLayer
  | WindAnimationLayer
  | EarthquakeLayer
  | ThunderstormLayer;

export abstract class MapboxGeoJSONLayer<LayerDesc extends SupportedGeoJSONLayers> extends LayerBase<LayerDesc> {
  // Mapbox
  /**
   * A string ID for the MapBox layer.
   */
  readonly mapbox_id: string;
  readonly mapbox_source: string;

  // There is no actual officially available typing from Mapbox
  readonly _layerSourceCache: any;

  // Compare times by millis to avoid treating equivalent DateTime instances as changes.
  private timeChanged: ChangeDetector<number> = new ChangeDetector<number>();
  private geoJsonData: ChangeDetector<GeoJSONFeatureCollection | SynchrounousState> =
    new ChangeDetector<GeoJSONFeatureCollection>();
  private isPending: ChangeDetector<boolean> = new ChangeDetector<boolean>(false);

  private latestSourceData: AsyncResult<GeoJSONFeatureCollection> = createResolvedAsyncResult(emptyFeatureCollection);

  protected debouncedUpdateTrigger = debounce(
    () => {
      this.updateTrigger();
      // Note: We always trigger the debounce update at least every second
      // as WindAnimation's frequent update event prevents GeoJSON layers to render due to debounce.
    },
    300,
    { leading: true, maxWait: 1000 },
  );

  constructor(
    uid: number,
    props: LayerDesc,
    scene: ScenePublicApi,
    timezone: GuiTimeZone,
    type: "symbol" | "line" | "fill" = "symbol",
  ) {
    super(uid, props, scene, timezone);
    this.mapbox_id = this.humanReadableId();
    this.mapbox_source = safeFmt`source_${this.humanReadableId()}`;

    const map = scene.getMapboxMap();
    // Add an empty source.
    map.addSource(this.mapbox_source, emptyGeoJsonSource());

    map.addLayer({
      id: this.mapbox_id,
      source: this.mapbox_source,
      type,
    });

    const layer = map.getLayer(this.mapbox_id);
    this._layerSourceCache = map.style.getLayerSourceCache(layer);
  }

  abstract updateTrigger(): void;

  abstract peekLayerCache(request: BaseRequest): AsyncResult<GeoJSONFeatureCollection>;

  abstract createRequest(dateTimeWithOffset: DateTime): BaseRequest;

  get mapboxIndex(): string {
    return this.mapbox_id;
  }

  protected updateMapboxPaintProperties(props?: PaintSpecification) {
    const entries = Object.entries(props ?? {}) as Array<
      [keyof PaintSpecification, PaintSpecification[keyof PaintSpecification]]
    >;
    for (const entry of entries) {
      this.scene.getMapboxMap().setPaintProperty(this.mapbox_id, entry[0], entry[1]);
    }
  }

  protected updateMapboxLayoutProperties(props?: LayoutSpecification) {
    const entries = Object.entries(props ?? {}) as Array<
      [keyof LayoutSpecification, LayoutSpecification[keyof LayoutSpecification]]
    >;
    for (const entry of entries) {
      this.scene.getMapboxMap().setLayoutProperty(this.mapbox_id, entry[0], entry[1]);
    }
  }

  isRebuffering(time: DateTime): boolean {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(time),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);
    const { synchronous } = this.peekLayerCache(request);
    return synchronous === SynchrounousState.StillPending;
  }

  moveZIndex(beforeLayerId?: string) {
    const map = this.scene.getMapboxMap();
    map.moveLayer(this.mapbox_id, beforeLayerId);
  }

  /**
   * removeLayer removes MapboxGeoJSON source and layers.
   * see also LayerBase abstract removeLayer() method comment
   */
  removeLayer(): void {
    this.scene.getMapboxMap().removeLayer(this.mapbox_id);
    this.scene.getMapboxMap().removeSource(this.mapbox_source);
    if (this.props?.id != null) {
      clearLayerLoading(this.props.id);
    }
  }

  releaseLayer(): void {
    if (this.latestSourceData.synchronous === SynchrounousState.StillPending) {
      const { cancel } = this.latestSourceData;
      cancel?.();
    }
  }

  protected getGeoJsonRequestOwner(): string {
    return `${(this.scene as ScenePublicApi).id}:${this.mapbox_source}`;
  }

  protected cancelSupersededGeoJsonRequests(result: AsyncResult<GeoJSONFeatureCollection>): number {
    if (result.synchronous !== SynchrounousState.StillPending) {
      return 0;
    }

    return networkCaches.geojson_cache.cancelSupersededPendingGeoJsonRequests({
      owner: this.getGeoJsonRequestOwner(),
      keepPromise: result.asynchronous,
    });
  }

  protected computeBoundingBox(distance: number) {
    const map = this.scene.getMapboxMap();
    const canvas = map.getCanvas();
    const { verticalGridNum, horizontalGridNum } = computeGridDimensionFromStep(canvas, distance);
    const area = getAreaFromMap(map);
    return { area, height: verticalGridNum, width: horizontalGridNum };
  }

  /**
   * Used to construct a unique ID to indentify different layers, which gets passed as a MapBox source identifier.
   */
  abstract humanReadableId(): string;

  // /**
  //  * Gets the last AsyncResult object used to update the Mapbox source.
  //  * @see {MapboxGeoJSONLayer.updateSource}
  //  */
  // public getLatestSourceData(): AsyncResult<GeoJSONFeatureCollection> {
  //   return this.latestSourceData;
  // }

  /**
   * @param result Async GeoJSONFeatureCollecton to pass as a MapBox source.
   * @param dateTimeWithOffset
   * @param sourceUpdated optional callback
   * @param options
   * @returns
   */
  protected updateSource(
    result: AsyncResult<GeoJSONFeatureCollection>,
    dateTimeWithOffset: DateTime,
    sourceUpdated?: (data: GeoJSONFeatureCollection) => void,
    options?: { keepLastDataWhilePending?: boolean; timeChangedOverride?: boolean },
  ) {
    this.latestSourceData = result;
    const { synchronous: data, asynchronous } = result;

    if (this.isPending.changed(data === SynchrounousState.StillPending)) {
      if (this.props?.id != null) {
        reportLayerLoading(this.props.id, this.isPending.value ?? false);
      }
    }

    if (data === SynchrounousState.StillPending) {
      // Keep retrying until the data becomes ready or permanently failed.
      // We do not want to update the mapbox source data with the result here, because the timestamp/viewport
      // might have changed.

      asynchronous
        .then(() => {
          this.beforeRender();
        })
        .catch((e) => {
          if (!e.isCancelled) {
            this.beforeRender();
          }
        });
    }

    const timeChanged = options?.timeChangedOverride ?? this.timeChanged.changed(dateTimeWithOffset.toMillis());
    if (this.geoJsonData.changed(data)) {
      if (data === SynchrounousState.NotRequested) {
        return;
      }
      if (data === SynchrounousState.StillPending) {
        if (timeChanged && !options?.keepLastDataWhilePending) {
          // Only empty the viewport if the time has changed.
          this.emptyMapboxSource();
        }
        return;
      }
      if (data === SynchrounousState.PermanentlyFailed) {
        // Nothing to do here.
        this.emptyMapboxSource();
        return;
      }
      // Execute callback indicating updated data
      sourceUpdated?.(data);
      // Update data.
      this.updateMapboxSource(data);
      return;
    }
    return;
  }

  /**
   * Update the associated mapbox source.
   * @param data
   * @private
   */
  private updateMapboxSource(data: GeoJSON.FeatureCollection<GeoJSON.Geometry>) {
    const source = this.scene.getMapboxMap().getSource(this.mapbox_source) as GeoJSONSource;
    if (!source) {
      logger.error("GeoJSON layer source not found!", this.mapbox_source);
      return;
    }
    source.setData(data);
  }

  /**
   * Helper function to create an empty mapbox source.
   * @private
   */
  private emptyMapboxSource() {
    this.updateMapboxSource(emptyFeatureCollection);
  }

  // Protected getters to expose the private properties to subclasses
  protected getMapboxSource(): string {
    return this.mapbox_source;
  }

  protected getLatestSourceData() {
    return this.latestSourceData;
  }

  protected getTimeChanged() {
    return this.timeChanged;
  }

  protected getGeoJsonData() {
    return this.geoJsonData;
  }

  protected invokeEmptyMapboxSource() {
    this.emptyMapboxSource();
  }

  protected invokeUpdateMapboxSource(data: GeoJSON.FeatureCollection<GeoJSON.Geometry>) {
    this.updateMapboxSource(data);
  }
}
